import { AxiosError } from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import Btn from '../../../../../components/Buttons/Btn';
import Modal from '../../../../../components/Modal';
import SingleSelect from '../../../../../components/SingleSelect';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../components/Notification';
import HttpStatus from '../../../../../enums/httpStatus';
import { databaseManagementUpdateFromFileApi } from '../../../../../services/requests';

type DatabaseManagementTable = {
  label: string;
  value: string;
  updateType?: {
    incremental: boolean;
    completeReplacement: boolean;
  };
};

type Option = { value: string; label: string };
type UpdateTypeKey = 'incremental' | 'completeReplacement';

const UPDATE_TYPE_LABELS: Record<UpdateTypeKey, string> = {
  incremental: 'databaseManagementUpdateTypeIncremental',
  completeReplacement: 'databaseManagementUpdateTypeCompleteReplacement',
};

type Props = {
  showModal: boolean;
  closeModal: () => void;
  table: DatabaseManagementTable;
  onQueued?: () => void;
};

const UpdateFromFileModal: React.FC<Props> = ({ showModal, closeModal, table, onQueued }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const fileTypeOptions: Option[] = useMemo(
    () => [
      {
        value: 'csv',
        label: 'CSV',
      },
    ],
    [],
  );

  const separatorOptions: Option[] = useMemo(
    () => [
      {
        value: 'comma',
        label: t('comma'),
      },
      {
        value: 'semicolon',
        label: t('semicolon'),
      },
    ],
    [t],
  );

  const [fileType, setFileType] = useState<Option>(fileTypeOptions[0]);
  const [separatorType, setSeparatorType] = useState<Option>(separatorOptions[1]);
  const [fileName, setFileName] = useState<string>('');
  const [updateType, setUpdateType] = useState<Option | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isCsv = (fileType?.value ?? '').toLowerCase() === 'csv';

  const updateTypeOptions: Option[] = useMemo(
    () => (['incremental', 'completeReplacement'] as UpdateTypeKey[])
      .filter((type) => table.updateType?.[type] === true)
      .map((type) => ({
        value: type,
        label: t(UPDATE_TYPE_LABELS[type]),
      })),
    [table.updateType, t],
  );

  const hasAvailableUpdateType = updateTypeOptions.length > 0;

  useEffect(() => {
    setUpdateType(updateTypeOptions[0] ?? null);
    setShowConfirmationModal(false);
  }, [showModal, updateTypeOptions]);

  const resetFile = () => {
    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setFileName('');
      return;
    }

    setFileName(file.name);
  };

  const handleSubmitUpdate = async () => {
    try {
      const file = fileInputRef.current?.files?.[0];
      if (!file) {
        frontendNotification({
          message: t('selectAFile'),
          type: 'warning',
        });
        return;
      }

      if (!hasAvailableUpdateType || !updateType?.value) {
        frontendNotification({
          message: t('databaseManagementNoUpdateTypeAvailable'),
          type: 'warning',
        });
        return;
      }

      setIsLoading(true);
      databaseManagementUpdateFromFileApi.query = '';

      const payload: Record<string, any> = {
        tableName: table.value,
        updateType: updateType.value,
      };

      if (isCsv) {
        payload.separatorType = separatorType.value;
      }

      const response = await databaseManagementUpdateFromFileApi.postWithFile(
        file,
        'file',
        payload,
      );

      if (response.status !== HttpStatus.OK) {
        throw response;
      }

      frontendNotification({
        message: response.data?.message ?? t('updateQueued'),
        type: 'success',
      });

      onQueued?.();
      resetFile();
      setShowConfirmationModal(false);
      closeModal();
    } catch (err) {
      if (!databaseManagementUpdateFromFileApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenConfirmation = () => {
    const file = fileInputRef.current?.files?.[0];

    if (!file) {
      frontendNotification({
        message: t('selectAFile'),
        type: 'warning',
      });
      return;
    }

    if (!hasAvailableUpdateType || !updateType?.value) {
      frontendNotification({
        message: t('databaseManagementNoUpdateTypeAvailable'),
        type: 'warning',
      });
      return;
    }

    setShowConfirmationModal(true);
  };

  const disableAction = () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return true;

    if (!hasAvailableUpdateType || !updateType?.value) return true;

    return false;
  };

  const handleChooseFile = () => {
    if (isLoading) return;

    fileInputRef.current?.click();
  };

  const selectedUpdateType = updateType?.value as UpdateTypeKey | undefined;

  const confirmationDescription = selectedUpdateType === 'incremental'
    ? t('databaseManagementConfirmUpdateIncrementalDescription')
    : t('databaseManagementConfirmUpdateCompleteReplacementDescription');

  return (
    <>
      <Modal
        title={`${t('updateFromFile')} - ${t(table.label)}`}
        showModal={showModal}
        closeModal={closeModal}
        cancelButton={closeModal}
        action={handleOpenConfirmation}
        actionNameBtn={t('update')}
        isLoading={isLoading}
        disableActionBtn={disableAction()}
        bodyStyle={{ paddingLeft: '1rem', paddingRight: '1rem' }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          margin: '1rem'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontWeight: 600 }}>{t('fileType')}</div>
            <SingleSelect
              options={fileTypeOptions}
              value={fileType}
              onChange={(opt) => setFileType(opt as Option)}
              placeholder={t('select')}
              disabled={isLoading}
            />
          </div>

          {hasAvailableUpdateType ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ fontWeight: 600 }}>{t('databaseManagementUpdateType')}</div>
              <SingleSelect
                options={updateTypeOptions}
                value={updateType}
                onChange={(opt) => setUpdateType(opt as Option)}
                placeholder={t('select')}
                disabled={isLoading || updateTypeOptions.length <= 1}
              />
              <div style={{ opacity: 0.85, fontSize: '0.85rem' }}>
                {t('databaseManagementUpdateTypeHelp')}
              </div>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                border: `1px solid ${theme.colors.negative}30`,
                background: `${theme.colors.negative}10`,
                color: theme.colors.text,
              }}
            >
              <div style={{ fontWeight: 600 }}>{t('databaseManagementUpdateType')}</div>
              <div>{t('databaseManagementNoUpdateTypeAvailable')}</div>
            </div>
          )}

          {isCsv && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ fontWeight: 600 }}>{t('csvDelimiter')}</div>
              <SingleSelect
                options={separatorOptions}
                value={separatorType}
                onChange={(opt) => setSeparatorType(opt as Option)}
                placeholder={t('select')}
                disabled={isLoading}
              />
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontWeight: 600 }}>{t('file')}</div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept={isCsv ? '.csv,text/csv' : undefined}
              disabled={isLoading}
              style={{ display: 'none' }}
            />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap',
              }}
            >
              <Btn
                text={`${t('select')} ${t('file')}`}
                onClick={handleChooseFile}
                disabled={isLoading}
                style={{ maxWidth: '180px' }}
              />

              <div
                style={{
                  flex: 1,
                  minWidth: '220px',
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  border: `1px solid ${theme.colors.onBackground}20`,
                  background: theme.colors.surface,
                  color: theme.colors.text,
                  fontSize: '0.9rem',
                }}
              >
                {fileName ? fileName : t('noFileSelected')}
              </div>
            </div>

          </div>

          <div style={{ opacity: 0.85, fontSize: '0.9rem' }}>{t('updateFromFileHelp')}</div>
        </div>
      </Modal>

      <Modal
        title={t('databaseManagementConfirmUpdateTitle')}
        showModal={showConfirmationModal}
        closeModal={() => setShowConfirmationModal(false)}
        cancelButton={() => setShowConfirmationModal(false)}
        action={handleSubmitUpdate}
        actionNameBtn={t('continue')}
        isLoading={isLoading}
        disableActionBtn={!updateType?.value}
        style={{ maxWidth: '460px' }}
        bodyStyle={{ paddingLeft: '1rem', paddingRight: '1rem' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', margin: '1rem' }}>
          <div style={{ fontWeight: 600 }}>
            {t('databaseManagementUpdateType')}: {updateType?.label}
          </div>
          <div style={{ lineHeight: 1.5 }}>{confirmationDescription}</div>
        </div>
      </Modal>
    </>
  );
};

export default UpdateFromFileModal;
